import { compiledPaths } from '@app/paths';
import { useLookbookOptions } from '@app/utils/hooks/lookbook/useLookbookOptions';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Row } from '@tanstack/react-table';
import { DeliveryWindow, Lookbook, Market } from '@typings';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card } from '../various/Card';

import { useLookbookListingContext } from './context/LookbookListingContext';

interface Props {
  row: Row<Lookbook.ListingItem>;
  markets: Market[];
  owners: Lookbook.Owner[];
  deliveryWindows: DeliveryWindow.Mixed[];
}

export const LookbookCard = ({ row, markets, owners, deliveryWindows }: Props) => {
  const { t } = useTranslation(['common']);
  const {
    lookbook,
    deliveryWindows: deliveryWindowIds = [],
    market: marketId,
    name = '-',
    dateCreated,
    owner: ownerId,
    dateLastVisited,
    visitedCount,
  } = row.original;
  const { setSelectedLookbookId } = useLookbookListingContext();
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'lookbook' });

  const marketName = markets.find(({ market }) => marketId === market)?.name;
  const ownerName = owners.find(({ owner }) => ownerId === owner)?.name;

  const deliveryWindowsNames =
    isEmpty(deliveryWindowIds) ?
      t('common:all')
    : deliveryWindowIds
        .map(id => deliveryWindows.find(({ deliveryWindow }) => deliveryWindow === id)?.name)
        .filter(isDefined)
        .join(', ');

  const dropdownItems = useLookbookOptions({ lookbookId: lookbook });

  const handleClick = () => {
    setSelectedLookbookId(lookbook ?? null);
  };

  return (
    <Card.Wrapper>
      <Card.Header>{t('common:market')}:</Card.Header>
      <Card.Title>
        <Link to={compiledPaths.LOOKBOOK_SINGLE({ lookbookId: lookbook })} className="absolute-cursor-pointer">
          {marketName}
        </Link>
      </Card.Title>
      <Card.AttributeList>
        <Card.Attribute name={t('common:name')} value={name} />
        {columnsVisibility.deliveryWindows?.isVisible && (
          <Card.Attribute name={t('common:delivery_window_other')} value={deliveryWindowsNames} />
        )}
        <Card.Attribute name={t('common:owner')} value={ownerName} />
        {columnsVisibility.dateCreated?.isVisible && <Card.Attribute name={t('common:created')} value={dateCreated} />}
        {columnsVisibility.dateLastVisited?.isVisible && <Card.Attribute name={t('common:last_visited')} value={dateLastVisited} />}
      </Card.AttributeList>
      {columnsVisibility.visitedCount?.isVisible && (
        <Card.AttributeList>
          <Card.Attribute name={t('common:visited_count')} value={visitedCount} />
        </Card.AttributeList>
      )}
      <Card.Menu title={t('common:option_other')} items={dropdownItems} className="z-interactiveTableItem" onClick={handleClick} />
    </Card.Wrapper>
  );
};
